import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { sendContact } from "../lib/api";

const topics = ["Guest pitch", "Collaboration", "Media", "Listener note"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", topic: "Guest pitch", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await sendContact(form);
      toast.success(res.message || "Message sent.");
      setForm({ name: "", email: "", topic: "Guest pitch", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full bg-transparent border-b border-white/20 py-3 px-1 text-white placeholder-white/30 focus:border-blaze focus:outline-none transition-colors";

  return (
    <section id="contact" className="relative py-24 sm:py-32 bg-charcoal/40" data-testid="contact-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
        <div>
          <p className="overline text-blaze mb-4">Contact · Guest inquiries</p>
          <h2 className="font-heading font-800 tracking-tight text-4xl sm:text-5xl leading-tight">
            Bring a story that can unlock someone else.
          </h2>
          <p className="mt-6 text-white/70 leading-relaxed">
            For collaborations, guest pitches, media, and listener notes — send a message and
            the Motivation Unlocked team will follow up.
          </p>
          <a
            href="mailto:Use the contact form below"
            className="mt-8 inline-flex items-center gap-3 text-white/80 hover:text-blaze transition-colors"
            data-testid="contact-email-link"
          >
            <span className="rounded-full border border-white/20 p-3">
              <Mail size={16} />
            </span>
            Use the contact form below
          </a>

          <div className="mt-8 flex flex-wrap gap-2">
            {["Personal transformation", "Leadership & discipline", "Faith & purpose", "Resilience"].map((t) => (
              <span key={t} className="rounded-full border border-white/10 px-3 py-1.5 text-xs text-white/50">
                {t}
              </span>
            ))}
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="rounded-3xl border border-white/10 bg-ink p-7 space-y-6"
          data-testid="contact-form"
        >
          <input
            required
            placeholder="Your name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={field}
            data-testid="contact-name-input"
          />
          <input
            required
            type="email"
            placeholder="Your email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={field}
            data-testid="contact-email-input"
          />
          <div>
            <label className="overline text-white/40">Topic</label>
            <div className="mt-3 flex flex-wrap gap-2">
              {topics.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setForm({ ...form, topic: t })}
                  className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                    form.topic === t
                      ? "bg-blaze text-white"
                      : "border border-white/20 text-white/60 hover:bg-white/10"
                  }`}
                  data-testid={`contact-topic-${t.split(" ")[0].toLowerCase()}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <textarea
            required
            rows={4}
            placeholder="Your message"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className={`${field} resize-none`}
            data-testid="contact-message-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-blaze px-6 py-3.5 text-sm font-bold text-white transition-transform hover:scale-[1.02] hover:blaze-glow disabled:opacity-50"
            data-testid="contact-submit-btn"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <>Send Message <Send size={15} /></>}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
